import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { RenditionsPanel } from '../components/RenditionsPanel';
import { PhotoPricing } from '../components/PhotoPricing';
import { formatDate, formatUsd } from '../lib/format';
import type { Collection } from '../components/CollectionsPanel';
import {
  AssignmentCell,
  hasInvalidOverride,
  toAssignmentList,
  type Assignments,
  type CatalogSize,
} from '../components/PrintSizeAssignment';

interface RenditionResult {
  original_key: string;
  src: string;
  thumb_src: string;
  width?: number;
  height?: number;
  display_url: string;
  thumb_url: string;
}

// Images binding inputs are capped at 20 MB; above this the browser sends a
// downscaled proxy that the API builds renditions from instead.
const PROXY_THRESHOLD_BYTES = 15 * 1024 * 1024;
const PROXY_MAX_EDGE = 3000;
// Cloudflare's request body limit on Free/Pro plans.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

interface Photo {
  uuid: string;
  /** R2 key of the watermarked display rendition (or a legacy URL). */
  src: string;
  thumb_src: string | null;
  /** Private print master in R2; null for legacy photos not yet upgraded. */
  original_key: string | null;
  renditions_updated_at: string | null;
  display_url?: string;
  thumb_url?: string;
  width: number;
  height: number;
  dpi: number;
  title: string;
  description: string;
  photographer: string;
  price_base_cents: number;
  slug: string;
  is_active: number;
  deactivated_at?: string | null;
  /** Set when purged with sales history: only the thumbnail is kept. */
  purged_at?: string | null;
}

interface PurgePlan {
  mode: 'delete' | 'keep_thumbnail';
  order_count: number;
  blockers: { code: string; message: string }[];
  file_count: number;
}

// Fields a purged photo can no longer change (the API rejects them).
const PURGE_LOCKED_FIELDS = ['src', 'thumb_src', 'original_key', 'is_active'] as const;

export function PhotoEdit() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const isNew = !uuid;
  const [reloadKey, setReloadKey] = useState(0);
  const [photo, setPhoto] = useState<Partial<Photo>>({
    src: '',
    width: 800,
    height: 600,
    dpi: 300,
    title: '',
    description: '',
    photographer: '',
    price_base_cents: 4500,
    slug: '',
    is_active: 1,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sizes, setSizes] = useState<CatalogSize[]>([]);
  // null until loaded (and always for a new photo — the API auto-assigns on create).
  const [assignments, setAssignments] = useState<Assignments | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  // Collection uuids this photo is in; null until loaded.
  const [memberOf, setMemberOf] = useState<string[] | null>(null);
  const isPurged = !!photo.purged_at;

  const applyRenditions = (r: RenditionResult) =>
    setPhoto((p) => ({
      ...p,
      src: r.src,
      thumb_src: r.thumb_src,
      original_key: r.original_key,
      display_url: r.display_url,
      thumb_url: r.thumb_url,
      width: r.width || p.width,
      height: r.height || p.height,
    }));

  // The master goes up untouched and stays private; the API returns watermarked
  // renditions for the storefront. Takes effect when the photo is saved.
  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Master is over 100 MB — export a smaller JPEG/PNG (Cloudflare request limit).');
      return;
    }
    try {
      setUploading('Reading image…');
      const dims = await readImageDimensions(file);
      if (!dims) throw new Error('Could not read the image — use a JPEG or PNG');
      const proxy = file.size > PROXY_THRESHOLD_BYTES ? await makeProxy(file) : null;

      setUploading('Uploading original…');
      const { asset_id, original_key } = await api.uploadRaw<{ asset_id: string; original_key: string }>(
        '/api/admin/photos/upload/original',
        file
      );

      setUploading('Building watermarked previews…');
      const form = new FormData();
      form.append('original_key', original_key);
      form.append('width', String(dims.width));
      form.append('height', String(dims.height));
      if (proxy) form.append('proxy', proxy, 'proxy.jpg');
      applyRenditions(
        await api.upload<RenditionResult>(`/api/admin/photos/upload/${asset_id}/renditions`, form)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(null);
    }
  };

  // Saved immediately: rebuilds renditions from the stored original, or moves a
  // legacy photo's public master into private storage first.
  const onRegenerate = async () => {
    if (!uuid) return;
    setError(null);
    const isLegacy = !photo.original_key;
    setUploading(isLegacy ? 'Moving original to private storage…' : 'Regenerating previews…');
    const path = `/api/admin/photos/${uuid}/renditions`;
    try {
      try {
        applyRenditions(await api.post<RenditionResult>(path));
      } catch (err) {
        // Over 20 MB: a legacy master is still public, so downscale it here and
        // retry. A private original can't be read back — it must be re-uploaded.
        if (!(err instanceof ApiError && err.code === 'proxy_required')) throw err;
        if (!isLegacy || !photo.display_url) {
          throw new Error('Original is over 20 MB — upload the file again to rebuild its previews.');
        }
        setUploading('Large original — building a preview proxy…');
        const res = await fetch(photo.display_url);
        if (!res.ok) throw new Error(`Could not load the current image (${res.status})`);
        const form = new FormData();
        form.append('proxy', await makeProxy(await res.blob()), 'proxy.jpg');
        setUploading('Moving original to private storage…');
        applyRenditions(await api.upload<RenditionResult>(path, form));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Regeneration failed');
    } finally {
      setUploading(null);
    }
  };

  useEffect(() => {
    if (isNew) return;
    api
      .get<{ photos: Photo[] }>(`/api/admin/photos`)
      .then((d) => {
        const found = d.photos.find((p) => p.uuid === uuid);
        if (found) setPhoto(found);
      })
      .catch(console.error);
    api
      .get<{ assignments: { size_id: string; min_dpi_override: number | null }[] }>(
        `/api/admin/photos/${uuid}/print-sizes`
      )
      .then((d) =>
        setAssignments(
          Object.fromEntries(
            d.assignments.map((a) => [a.size_id, { min_dpi_override: a.min_dpi_override }])
          )
        )
      )
      .catch(console.error);
  }, [uuid, isNew, reloadKey]);

  // A new photo starts in the key collection, matching the API's default.
  useEffect(() => {
    api
      .get<{ collections: Collection[] }>('/api/admin/collections')
      .then((d) => {
        setCollections(d.collections);
        setMemberOf(
          d.collections
            .filter((c) => (isNew ? c.is_key : c.photo_uuids.includes(uuid!)))
            .map((c) => c.uuid)
        );
      })
      .catch(console.error);
  }, [uuid, isNew]);

  useEffect(() => {
    api
      .get<{ sizes: CatalogSize[] }>('/api/admin/print-sizes')
      .then((d) => setSizes(d.sizes))
      .catch(console.error);
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        await api.post('/api/admin/photos', memberOf ? { ...photo, collection_uuids: memberOf } : photo);
      } else {
        if (assignments && hasInvalidOverride(assignments)) {
          throw new Error('DPI overrides must be between 72 and 600.');
        }
        const body: Partial<Photo> = { ...photo };
        if (isPurged) for (const k of PURGE_LOCKED_FIELDS) delete body[k];
        await api.put(`/api/admin/photos/${uuid}`, body);
        if (assignments && !isPurged) {
          await api.put(`/api/admin/photos/${uuid}/print-sizes`, {
            assignments: toAssignmentList(assignments),
          });
        }
        if (memberOf) {
          await api.put(`/api/admin/photos/${uuid}/collections`, { collection_uuids: memberOf });
        }
      }
      navigate('/photos');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!uuid || !confirm('Deactivate this photo?')) return;
    await api.delete(`/api/admin/photos/${uuid}`);
    navigate('/photos');
  };

  // The API decides what a purge does (and whether it's allowed yet) from the
  // photo's sales history, so ask it for the plan before confirming.
  const onPurge = async () => {
    if (!uuid) return;
    setError(null);
    try {
      const plan = await api.get<PurgePlan>(`/api/admin/photos/${uuid}/purge`);
      if (plan.blockers.length) {
        setError(`Can't purge yet: ${plan.blockers.map((b) => b.message).join(' ')}`);
        return;
      }
      const what =
        plan.mode === 'delete'
          ? `This photo has no sales, so it will be deleted completely — the record and all ${plan.file_count} stored files.`
          : `This photo is in ${plan.order_count} order${plan.order_count === 1 ? '' : 's'}. Order history is kept with the watermarked thumbnail; the print master${plan.file_count === 1 ? '' : 's'}, proxies and display images (${plan.file_count} files) are deleted, and it can never be sold or reprinted again.`;
      const typed = prompt(`${what}\n\nThis can't be undone. Type "purge" to confirm.`);
      if (typed?.trim().toLowerCase() !== 'purge') return;
      setBusy(true);
      const r = await api.post<{ mode: PurgePlan['mode'] }>(`/api/admin/photos/${uuid}/purge`);
      if (r.mode === 'delete') navigate('/photos');
      else setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purge failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      <Link to="/photos" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Photos
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 24px' }}>
        {isNew ? 'New photo' : photo.title}
      </h1>
      {isPurged && (
        <p style={{ maxWidth: 720, margin: '0 0 16px', padding: 12, fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b' }}>
          Purged {formatDate(photo.purged_at)}. Only the watermarked thumbnail is kept, for order history —
          the print master is gone, so this photo can't be sold or reprinted.
        </p>
      )}

      <form
        onSubmit={onSubmit}
        style={{ background: '#fff', padding: 24, border: '1px solid #e5e5e5', borderRadius: 8, maxWidth: 720 }}
      >
        <CollectionsPicker collections={collections} selected={memberOf} onChange={setMemberOf} />
        <Field label="Title" value={photo.title || ''} onChange={(v) => setPhoto({ ...photo, title: v })} />
        <Field label="Slug (URL-safe)" value={photo.slug || ''} onChange={(v) => setPhoto({ ...photo, slug: v })} />
        {!isPurged && (
          <ImagePanel
            photo={photo}
            busy={uploading}
            canRegenerate={!isNew && !!photo.src}
            onFileSelected={onFileSelected}
            onRegenerate={onRegenerate}
          />
        )}
        <Field
          label="Photographer"
          value={photo.photographer || ''}
          onChange={(v) => setPhoto({ ...photo, photographer: v })}
        />
        <FieldTextarea
          label="Description"
          value={photo.description || ''}
          onChange={(v) => setPhoto({ ...photo, description: v })}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <NumberField
            label={`Base price (cents) = ${formatUsd(photo.price_base_cents)}`}
            value={photo.price_base_cents || 0}
            onChange={(v) => setPhoto({ ...photo, price_base_cents: v })}
          />
          <NumberField label="Width (px)" value={photo.width || 0} onChange={(v) => setPhoto({ ...photo, width: v })} />
          <NumberField label="Height (px)" value={photo.height || 0} onChange={(v) => setPhoto({ ...photo, height: v })} />
          <NumberField label="Native DPI" value={photo.dpi || 300} onChange={(v) => setPhoto({ ...photo, dpi: v })} />
        </div>

        {!isPurged && (
          <>
            <PrintSizesPanel
              photo={photo}
              isNew={isNew}
              sizes={sizes}
              assignments={assignments}
              onChange={setAssignments}
            />
            {!isNew && assignments && !!photo.width && !!photo.height && (
              <PhotoPricing
                photo={{ width: photo.width, height: photo.height, price_base_cents: photo.price_base_cents || 0 }}
                sizes={sizes}
                assignments={assignments}
              />
            )}
            <label style={{ display: 'block', marginTop: 16, fontSize: 14 }}>
              <input
                type="checkbox"
                checked={photo.is_active === 1}
                onChange={(e) => setPhoto({ ...photo, is_active: e.target.checked ? 1 : 0 })}
              />{' '}
              Active (visible in storefront)
            </label>
          </>
        )}

        {error && <p style={{ color: '#b91c1c', marginTop: 16 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button type="submit" disabled={busy || !!uploading || !photo.src} style={btnPrimary}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {!isNew && !isPurged && (
            <button type="button" onClick={onDelete} disabled={busy} style={btnDanger}>
              Deactivate
            </button>
          )}
          {!isNew && !isPurged && (
            <button type="button" onClick={onPurge} disabled={busy || !!uploading} style={btnDanger}>
              Purge…
            </button>
          )}
        </div>
        {!isNew && !isPurged && (
          <p style={{ fontSize: 12, color: '#6b7280', margin: '12px 0 0' }}>
            Purge frees storage for a retired photo: it must be deactivated for 25 hours and have no
            unshipped orders. Photos with sales keep only their thumbnail for order history; photos
            without sales are deleted completely.
          </p>
        )}
      </form>

      <RenditionsPanel photo={photo} />
    </div>
  );
}

/** Toggle chips: which collections the photo appears in. Saved with the form. */
function CollectionsPicker({
  collections,
  selected,
  onChange,
}: {
  collections: Collection[];
  selected: string[] | null;
  onChange: (uuids: string[]) => void;
}) {
  if (!selected) return null;
  const toggle = (uuid: string) =>
    onChange(selected.includes(uuid) ? selected.filter((u) => u !== uuid) : [...selected, uuid]);
  const inKey = collections.some((c) => c.is_key && selected.includes(c.uuid));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Collections</div>
      <div role="group" aria-label="Collections" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {collections.map((c) => {
          const on = selected.includes(c.uuid);
          return (
            <button
              key={c.uuid}
              type="button"
              aria-pressed={on}
              onClick={() => toggle(c.uuid)}
              title={
                c.is_key
                  ? 'Key collection — shown on the landing page'
                  : c.system_key === 'about'
                    ? 'Can be placed on the About page'
                    : `/collections/${c.slug}`
              }
              style={{
                padding: '5px 12px',
                borderRadius: 999,
                fontSize: 13,
                cursor: 'pointer',
                border: `1px solid ${on ? '#1a1a1a' : '#d4d4d4'}`,
                background: on ? '#1a1a1a' : '#fff',
                color: on ? '#fff' : '#374151',
              }}
            >
              {on ? '✓ ' : ''}
              {c.is_key ? '★ ' : ''}
              {c.name}
            </button>
          );
        })}
        {collections.length === 0 && (
          <span style={{ fontSize: 13, color: '#9ca3af' }}>No collections yet — create them on the Photos page.</span>
        )}
      </div>
      {collections.length > 0 && !inKey && (
        <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>Not in the key collection — won't show on the landing page.</div>
      )}
    </div>
  );
}

function PrintSizesPanel({
  photo,
  isNew,
  sizes,
  assignments,
  onChange,
}: {
  photo: Partial<Photo>;
  isNew: boolean;
  sizes: CatalogSize[];
  assignments: Assignments | null;
  onChange: (next: Assignments) => void;
}) {
  const width = photo.width || 0;
  const height = photo.height || 0;
  const dpi = photo.dpi || 300;
  if (!width || !height) return null;
  const p = { width, height };

  return (
    <div style={{ margin: '4px 0 16px', padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Master prints at{' '}
          <strong>
            {(width / dpi).toFixed(1)}″ × {(height / dpi).toFixed(1)}″
          </strong>{' '}
          at native {dpi} DPI.
        </div>
        <Link to="/print-sizes" style={{ fontSize: 12, color: '#2563eb' }}>
          Manage sizes
        </Link>
      </div>
      {isNew || !assignments ? (
        <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>
          On save, the photo is assigned every active size it prints at the size's minimum DPI. You
          can then adjust sizes and per-size DPI overrides here.
        </p>
      ) : (
        <>
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
            Sizes offered for this photo. Set a number to override the minimum DPI for that size —
            leave blank for the default.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {sizes.map((size) => (
              <AssignmentCell
                key={size.id}
                photo={p}
                size={size}
                assignment={assignments[size.id]}
                basePriceCents={photo.price_base_cents || 0}
                onChange={(next) => {
                  const { [size.id]: _removed, ...rest } = assignments;
                  onChange(next ? { ...rest, [size.id]: next } : rest);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ImagePanel({
  photo,
  busy,
  canRegenerate,
  onFileSelected,
  onRegenerate,
}: {
  photo: Partial<Photo>;
  busy: string | null;
  canRegenerate: boolean;
  onFileSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRegenerate: () => void;
}) {
  const preview = photo.thumb_url || photo.display_url;
  const isLegacy = !!photo.src && !photo.original_key;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Print master (JPEG or PNG)</div>
      {preview && (
        <a href="#previews" style={{ display: 'inline-flex', gap: 10, alignItems: 'center', marginBottom: 8, fontSize: 12, color: '#2563eb' }}>
          <img src={preview} alt="" style={{ height: 56, borderRadius: 4, display: 'block' }} />
          All files and customer previews ↓
        </a>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <input type="file" accept="image/jpeg,image/png" onChange={onFileSelected} disabled={!!busy} />
        {canRegenerate && (
          <button type="button" onClick={onRegenerate} disabled={!!busy} style={btnSecondary}>
            {isLegacy ? 'Make original private' : 'Regenerate previews'}
          </button>
        )}
        {busy && <span style={{ fontSize: 13, color: '#6b7280' }}>{busy}</span>}
      </div>
      <p style={{ fontSize: 12, color: isLegacy ? '#b45309' : '#6b7280', margin: '8px 0 0' }}>
        {isLegacy
          ? 'Legacy photo: the full-resolution file is publicly served. Make it private to store it as the print master and show watermarked previews instead.'
          : photo.original_key
            ? 'The original is stored privately and only sent to Gelato for printing. The storefront shows watermarked previews.'
            : 'Upload the full-resolution file. It stays private; the storefront shows watermarked previews.'}
      </p>
    </div>
  );
}

// Downscaled JPEG the API builds renditions from when the master is too big
// for the Images binding. Never printed.
async function makeProxy(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, PROXY_MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not build preview proxy'))), 'image/jpeg', 0.9)
  );
}

function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </label>
  );
}

function FieldTextarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <textarea
        value={value}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
          fontFamily: 'inherit',
        }}
      />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          fontSize: 14,
        }}
      />
    </label>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #d4d4d4',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnDanger: React.CSSProperties = {
  padding: '10px 16px',
  background: '#fff',
  color: '#b91c1c',
  border: '1px solid #fca5a5',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
